import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Input,
  Label,
  PrimaryFullScreenSpinner,
  toast,
  useAppForm,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/products_/$productId_/unit-prices_/update",
)({
  head: () => opsHead("Update Unit Prices"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const updateUnitPricesFormSchema = z.object({
  unitPriceFile: z
    .instanceof(File, {
      message: "Unit price file is required.",
    })
    .optional(),
  clearExistingUnitPrices: z.boolean().default(false),
});

type FormValues = z.input<typeof updateUnitPricesFormSchema>;

const fileToBase64 = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

function View() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const productId = params.productId;

  if (!productId) {
    throw new Error("Missing product id");
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminUpdateUnitPricesProduct($productId: ID!) {
        product(id: $productId) {
          __typename
          id
          internalName
          website
          provider {
            id
            name
          }
          ... on FundProduct {
            unit {
              id
              unitPriceFileUrl
            }
          }
        }
      }
    `),
    { variables: { productId } },
  );

  const product = data.product;
  if (!product || product.__typename !== "FundProduct" || !product.unit) {
    throw new NotFoundError();
  }

  const [updateFundUnitPrices, { loading }] = useMutation(
    graphql(`
      mutation AdminUpdateFundUnitPrices($input: UpdateFundUnitPricesInput!) {
        updateFundUnitPrices(input: $input) {
          fundProduct {
            id
          }
        }
      }
    `),
  );

  const form = useAppForm({
    defaultValues: {
      unitPriceFile: undefined,
      clearExistingUnitPrices: false,
    } as FormValues,
    validators: {
      onSubmit: updateUnitPricesFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (!value.unitPriceFile) {
        toast({
          kind: "error",
          title: "Unit price file is required",
        });
        return;
      }

      const unitPriceFileBase64 = await fileToBase64(value.unitPriceFile);

      await updateFundUnitPrices({
        variables: {
          input: {
            productId: product.id,
            clearExistingUnitPrices: value.clearExistingUnitPrices ?? false,
            unitPriceFileBase64,
          },
        },
        onCompleted: () => {
          toast({
            kind: "success",
            title: "Unit prices updated",
          });
          navigate({ to: `/products/${product.id}/unit-prices` });
        },
        onError: (error) => {
          toast({
            kind: "error",
            title: "Failed to update unit prices",
            description:
              error.graphQLErrors?.[0]?.message ?? error.message ?? undefined,
          });
        },
      });
    },
  });

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title="Update Unit Prices"
        subtitle="Upload a unit price data file"
      />
      <form
        className="flex flex-col space-y-6"
        encType="multipart/form-data"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="flex flex-col items-start space-y-2">
            <Label>Provider</Label>
            <div className="text-base text-neutral-800">
              {product.provider?.name ?? "Unknown"}
            </div>
          </div>
          <div className="flex flex-col items-start space-y-2">
            <Label>Product</Label>
            <div>{product.internalName}</div>
          </div>
          <div className="flex flex-col items-start space-y-2">
            <Label>Links</Label>

            {product.website && (
              <a
                href={product.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-700 hover:underline"
              >
                Product website
              </a>
            )}

            {product.unit?.unitPriceFileUrl && (
              <a
                href={product.unit.unitPriceFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-700 hover:underline"
              >
                Download unit price file
              </a>
            )}
          </div>
          <form.AppField name="unitPriceFile">
            {(field) => (
              <div className="flex flex-col items-start space-y-2">
                <Label htmlFor="unitPriceFile">Unit price data</Label>
                <Input
                  id="unitPriceFile"
                  type="file"
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    field.handleChange(file ?? undefined);
                  }}
                />
              </div>
            )}
          </form.AppField>
          <form.AppField name="clearExistingUnitPrices">
            {(field) => (
              <div className="flex flex-col items-start space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    id="clearExistingUnitPrices"
                    type="checkbox"
                    name={field.name}
                    className="size-4 rounded border-neutral-300"
                    checked={Boolean(field.state.value)}
                    onChange={(event) =>
                      field.handleChange(event.currentTarget.checked)
                    }
                  />
                  <Label
                    htmlFor="clearExistingUnitPrices"
                    className="block font-medium text-neutral-900 text-sm leading-6"
                  >
                    Overwrite existing data?
                  </Label>
                </div>
              </div>
            )}
          </form.AppField>
          <div className="flex flex-row justify-between">
            <Button
              type="button"
              variant="secondary"
              disabled={loading}
              onClick={() =>
                navigate({ to: `/products/${product.id}/unit-prices` })
              }
            >
              Cancel
            </Button>
            <form.SubmitButton
              disabled={loading}
              childrenWhileSubmitting="Updating..."
            >
              Update
            </form.SubmitButton>
          </div>
        </form.AppForm>
      </form>
    </AdminPageContainer>
  );
}
